import { times } from 'lodash';
import {
  ALL_HOURS,
  WEEKEND,
  FROM_11PM_TO_7AM,
  FROM_7AM_TO_2PM,
  FROM_2PM_TO_6PM,
  FROM_6PM_TO_11PM,
  FROM_7AM_TO_11PM,
} from '../constants/';
import from11amto7am from './chargingPatterns/11am-7am.svg';
import from7amto11pm from './chargingPatterns/7am-11pm.svg';
import from7amto2pm from './chargingPatterns/7am-2pm.svg';
import from2pmto6pm from './chargingPatterns/2pm-6pm.svg';
import from6pmto11pm from './chargingPatterns/6pm-11pm.svg';
import from11pmTo7am from './chargingPatterns/11pm-7am.svg';
import allHoursImage from './chargingPatterns/all-hours.svg';

const DST = {
  2021: {
    startHour: 1610,
    endHour: 7322,
  },
};

const dstAdjustedHour = (hour, year) => {
  const { startHour, endHour } = DST[year];
  const isWithinDST = hour >= startHour && hour <= endHour;
  return isWithinDST ? (hour + 1) % 24 : hour % 24;
};

// NOTE: this is not a complete list of leap years, but is sufficient for the data
// provided
const isLeapYear = (year) => [2016, 2020, 2024].includes(year);
const hoursInYear = (year) => (isLeapYear(year) ? 8784 : 8760);

// NOTE: Need to be only monday thru friday.

export const chargingPatternsJuneToAug = [
  {
    id: FROM_7AM_TO_2PM,
    title: 'M-F: 7 a.m. to 2 p.m.',
    value: '$$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 7 && hour < 14
            ? 1.1
            : 0;
        }),
    },
    image: from7amto2pm,
    description: 'Recommended!',
  },
  {
    id: FROM_2PM_TO_6PM,
    title: 'M-F: 2 p.m. to 6 p.m.',
    value: '$$$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 14 && hour < 18
            ? 1.1
            : 0;
        }),
    },
    image: from2pmto6pm,
    description: 'Mid-rate period',
  },
  {
    id: FROM_6PM_TO_11PM,
    title: 'M-F: 6 p.m. to 11 p.m.',
    value: '$$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 18 && hour < 23
            ? 1.1
            : 0;
        }),
    },
    image: from6pmto11pm,
    description: 'Peak hours - highest rates',
  },
  {
    id: FROM_11PM_TO_7AM,
    title: 'M-F: 11 p.m. to 7 a.m.',
    value: '$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && (hour >= 23 || hour < 7)
            ? 1.1
            : 0;
        }),
    },
    image: from11pmTo7am,
    description: 'Evening rates',
  },

  {
    id: WEEKEND,
    title: 'Weekend',
    value: '$?',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0); // initiate with first day of the year
          date.setHours(i); // Add i hours to the start of the year
          const dayOfWeek = date.getDay(); // get week day, 0 is sunday and 6 is saturday
          return dayOfWeek === 0 || dayOfWeek === 6 ? 1.1 : 0;
        }),
    },
    image: allHoursImage,
    description: 'Only active on Saturdays and Sundays',
  },
  {
    id: ALL_HOURS,
    title: 'All Hours',
    value: '$?',
    loadProfile: {
      data: () => times(hoursInYear(2021), (_) => 1.1),
    },
    image: allHoursImage,
    description: 'Unpredictable!',
  },
];

export const chargingPatternsSepToMay = [
  {
    id: FROM_7AM_TO_11PM,
    title: 'M-F: 7 a.m. to 11 p.m.',
    value: '$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 7 && hour <= 23
            ? 1.1
            : 0;
        }),
    },
    image: from7amto11pm,
    description: 'Off-peak daytime rates',
  },
  {
    id: FROM_11PM_TO_7AM,
    title: 'M-F: 11 p.m to 7 a.m',
    value: '$',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0, 1, 0, 0, 0);
          date.setHours(i);
          const hour = dstAdjustedHour(date.getHours(), year);
          const dayOfWeek = date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5 && (hour >= 23 || hour < 7)
            ? 1.1
            : 0;
        }),
    },
    image: from11amto7am,
    description: 'Recommended!',
  },
  {
    id: WEEKEND,
    title: 'Weekend',
    value: '$?',
    loadProfile: {
      data: (year) =>
        times(hoursInYear(year), (i) => {
          const date = new Date(year, 0); // initiate with first day of the year
          date.setHours(i); // Add i hours to the start of the year
          const dayOfWeek = date.getDay(); // get week day, 0 is sunday and 6 is saturday
          return dayOfWeek === 0 || dayOfWeek === 6 ? 1.1 : 0;
        }),
    },
    image: allHoursImage,
    description: 'Only active on Saturdays and Sundays',
  },
  {
    id: ALL_HOURS,
    title: 'All Hours',
    value: '$?',
    loadProfile: {
      data: () => times(hoursInYear(2021), (_) => 1.1),
    },
    image: allHoursImage,
    description: 'Unpredictable!',
  },
];


export function combinedLoadProfile(sepMayLoadProfile, junAugLoadProfile) {
  return {
    data: (year) => {
      const hours = hoursInYear(year);

      // Pre-compute both load profiles once
      const sepMayData = sepMayLoadProfile.data(year);
      const junAugData = junAugLoadProfile.data(year);

      // Pre-calculate month boundaries in hours for the year
      const monthBoundaries = [];
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month, 1);
        const hourOfYear = Math.floor(
          (date.getTime() - new Date(year, 0, 1).getTime()) / (1000 * 60 * 60)
        );
        monthBoundaries.push(hourOfYear);
      }
      monthBoundaries.push(hours); // Add end of year

      const result = new Array(hours);

      // Process by month chunks instead of hour by hour
      for (let month = 0; month < 12; month++) {
        const startHour = monthBoundaries[month];
        const endHour = monthBoundaries[month + 1];
        const isMayToSep = month >= 4 && month <= 8; // May (4) to September (8)
        const sourceData = isMayToSep ? sepMayData : junAugData;

        // Copy the month's data in one operation
        for (let hour = startHour; hour < endHour; hour++) {
          result[hour] = sourceData[hour];
        }
      }

      return result;
    },
  };
}


